import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackgroundLayer from "../components/BackgroundLayer";
import StatusBar from "../components/StatusBar";
import BoardCanvas from "../components/war-room/BoardCanvas";
import ActionBoard from "../components/war-room/ActionBoard";
import { useCaseActionBoard } from "../hooks/useCaseActionBoard";
import { useCaseBoardSeats, type BoardSeat } from "../hooks/useBoardSeats";
import {
  useClaimsSummary,
  useSetClaimSurvival,
  isLive,
  type ClaimsSummary,
  type ClaimSummaryRow,
} from "../hooks/useClaimsSummary";

/**
 * DocketBoard — the O.K. Corral showdown. One card per party on the board; a
 * single dedicated frame below it shows the dossier for whichever fighter you
 * click (their claims + their docket). Nothing stacks.
 */
function DocketBoard() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { data: board } = useCaseActionBoard(caseId);
  const { data: seats } = useCaseBoardSeats(caseId);
  const { data: claims } = useClaimsSummary(caseId);
  const [selectedSeat, setSelectedSeat] = useState<string | null>(null);
  const [rearrange, setRearrange] = useState(false);

  const seat = seats?.find((s) => s.id === selectedSeat) ?? null;

  return (
    <div className="relative min-h-screen text-bone">
      <BackgroundLayer variant="modes" overlay="heavy" />
      <StatusBar />

      <main className="relative z-10 mx-auto max-w-6xl px-6 pb-24 pt-8">
        <button type="button" onClick={() => caseId && navigate(`/case/${caseId}`)}
          className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute hover:text-bone">
          ← back to the war room
        </button>

        <header className="mb-5 mt-4">
          <p className="font-display text-base font-light uppercase tracking-[0.18em] text-bone-mute">
            // docket // the showdown
          </p>
          <h1 className="mt-1 font-display text-3xl font-light tracking-tight text-bone">The Showdown</h1>
          <p className="mt-2 max-w-2xl font-display text-sm leading-relaxed text-bone-dim">
            Every party a gunfighter. The plaintiff&#39;s complaint is a volley of claims fired at the
            defendants — each one you defeat thins the line. Click a fighter to open their dossier below.
          </p>
        </header>

        <div className="mb-2 flex items-center justify-end">
          <button type="button" onClick={() => setRearrange((v) => !v)}
            className={`rounded border px-3 py-1 font-display text-sm font-light uppercase tracking-[0.1em] transition-colors ${
              rearrange ? "border-ember-bright/60 bg-ember/20 text-ember-hot"
                : "border-steel-dark bg-iron/50 text-bone-mute hover:text-bone-dim"
            }`}>
            {rearrange ? "done arranging" : "⇄ rearrange seats"}
          </button>
        </div>

        <div className="relative aspect-[1000/560] overflow-hidden rounded border border-steel-dark backdrop-blur-sm">
          <img src="/bg-frontier/board-bg.webp" alt="" draggable={false}
            className="absolute inset-0 h-full w-full select-none object-cover" />
          <div className="absolute inset-0"
            style={{ background: "linear-gradient(180deg, rgba(10,7,4,0.5) 0%, rgba(10,7,4,0.22) 38%, rgba(10,7,4,0.66) 100%)" }} />
          {board && seats ? (
            <div className="absolute inset-0">
              <BoardCanvas
                caseId={caseId!}
                segments={board.segments}
                seats={seats}
                live={claims?.live ?? 0}
                total={claims?.total ?? 0}
                counterclaims={claims?.counterclaims ?? []}
                selectedSeatId={selectedSeat}
                rearrange={rearrange}
                onSelect={(_partyId, seatId) => setSelectedSeat(seatId)}
              />
            </div>
          ) : (
            <p className="absolute inset-0 flex items-center justify-center font-display text-sm text-bone-mute">
              clearing the street…
            </p>
          )}
        </div>

        {/* dynamic dossier — follows the selected fighter */}
        <SubjectFrame caseId={caseId} seat={seat} claims={claims} />
      </main>
    </div>
  );
}

function roleLine(seat: BoardSeat): string {
  if (seat.group_label) return seat.group_label;
  if (seat.kind === "plaintiff") return "plaintiff · the aggressor";
  if (seat.kind === "outside") return "outside party · referenced, not served";
  if (seat.kind === "defendant") return "defendant";
  return seat.kind;
}

function SubjectFrame({
  caseId,
  seat,
  claims,
}: {
  caseId: string | undefined;
  seat: BoardSeat | null;
  claims: ClaimsSummary | undefined;
}) {
  if (!seat) {
    return (
      <div className="mt-4 rounded border border-steel-dark bg-iron/60 px-5 py-6 backdrop-blur-sm">
        <p className="font-display text-base font-light text-bone-dim">
          Click a fighter on the board to open their dossier.
        </p>
        {claims && (
          <p className="mt-1 font-display text-sm text-bone-mute">
            {claims.live} / {claims.total} rounds still live in the fight.
          </p>
        )}
      </div>
    );
  }

  const combatant = seat.kind === "plaintiff" || seat.kind === "defendant";
  const claimsTitle = seat.kind === "plaintiff" ? "rounds you're firing" : "rounds aimed at this defendant";

  // return volley — counterclaims this seat is involved in
  const allCcs = claims?.counterclaims ?? [];
  const ccs =
    seat.kind === "plaintiff"
      ? allCcs // every counterclaim is aimed at the plaintiff
      : seat.party_id
        ? allCcs.filter((c) => c.asserting_party_id === seat.party_id)
        : [];
  const ccTitle = seat.kind === "plaintiff" ? "counterclaims aimed back at you" : "your counterclaims (return volley)";

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div className="rounded border border-ember-bright/40 bg-iron/70 px-5 py-4 backdrop-blur-sm">
        <p className="font-display text-xl font-light tracking-tight text-bone">{seat.label}</p>
        <p className="font-display text-sm uppercase tracking-[0.12em] text-bone-mute">{roleLine(seat)}</p>
      </div>

      {combatant && claims && claims.claims.length > 0 && (
        <ClaimsRoster caseId={caseId} claims={claims.claims} live={claims.live} total={claims.total} title={claimsTitle} />
      )}

      {ccs.length > 0 && <CounterclaimRoster caseId={caseId} claims={ccs} title={ccTitle} />}

      {seat.party_id ? (
        <ActionBoard caseId={caseId!} focusPartyId={seat.party_id} />
      ) : (
        <div className="rounded border border-steel-dark/70 bg-iron/40 px-5 py-4">
          <p className="font-display text-sm text-bone-mute">
            Outside party — referenced in the record but not served. No responsive docket.
          </p>
        </div>
      )}
    </div>
  );
}

function ClaimsRoster({
  caseId,
  claims,
  live,
  total,
  title,
}: {
  caseId: string | undefined;
  claims: ClaimSummaryRow[];
  live: number;
  total: number;
  title: string;
}) {
  const setSurvival = useSetClaimSurvival(caseId);
  return (
    <div className="rounded border border-steel-dark bg-iron/60 p-4 backdrop-blur-sm">
      <div className="mb-3 flex items-baseline justify-between">
        <p className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim">{title}</p>
        <span className="font-display text-sm text-ember-hot">{live} / {total} live</span>
      </div>
      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        {claims.map((c) => {
          const alive = isLive(c.survival);
          return (
            <div key={c.id} className="flex items-center justify-between gap-3 rounded border border-steel-dark/70 bg-iron/40 px-3 py-2">
              <span className={`min-w-0 font-display text-sm ${alive ? "text-bone-dim" : "text-bone-mute/50 line-through"}`}>
                {c.claim_name}
              </span>
              <button type="button" disabled={setSurvival.isPending}
                onClick={() => setSurvival.mutate({ claimId: c.id, survival: alive ? "defeated" : "contested" })}
                className={`shrink-0 rounded border px-2 py-0.5 font-display text-sm transition-colors disabled:opacity-40 ${
                  alive ? "border-ember-bright/50 text-ember-hot hover:bg-ember/20"
                    : "border-steel-dark text-bone-mute hover:text-bone-dim"
                }`}>
                {alive ? "knock down" : "restore"}
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-2 font-display text-sm text-bone-mute/60">
        knocking a claim down marks it defeated and thins the volley on the board.
      </p>
    </div>
  );
}

function CounterclaimRoster({
  caseId,
  claims,
  title,
}: {
  caseId: string | undefined;
  claims: ClaimSummaryRow[];
  title: string;
}) {
  const setSurvival = useSetClaimSurvival(caseId);
  const live = claims.filter((c) => isLive(c.survival)).length;
  return (
    <div className="rounded border border-blue-bright/40 bg-iron/60 p-4 backdrop-blur-sm">
      <div className="mb-3 flex items-baseline justify-between">
        <p className="font-display text-base font-light uppercase tracking-[0.14em] text-blue-bright">{title}</p>
        <span className="font-display text-sm text-blue-bright">{live} / {claims.length} live</span>
      </div>
      <div className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
        {claims.map((c) => {
          const alive = isLive(c.survival);
          return (
            <div key={c.id} className="flex items-center justify-between gap-3 rounded border border-steel-dark/70 bg-iron/40 px-3 py-2">
              <span className={`min-w-0 font-display text-sm ${alive ? "text-bone-dim" : "text-bone-mute/50 line-through"}`}>
                {c.claim_name}
                {c.target ? <span className="text-bone-mute/60"> · vs {c.target}</span> : null}
              </span>
              <button type="button" disabled={setSurvival.isPending}
                onClick={() => setSurvival.mutate({ claimId: c.id, survival: alive ? "defeated" : "asserted" })}
                className={`shrink-0 rounded border px-2 py-0.5 font-display text-sm transition-colors disabled:opacity-40 ${
                  alive ? "border-ember-bright/50 text-ember-hot hover:bg-ember/20"
                    : "border-steel-dark text-bone-mute hover:text-bone-dim"
                }`}>
                {alive ? "knock down" : "restore"}
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-2 font-display text-sm text-bone-mute/60">
        counterclaims you raised in the answer — defeated if the plaintiff's motion against them is granted.
      </p>
    </div>
  );
}

export default DocketBoard;
